import { forwardRef, useState } from 'react';
import { usePopper } from 'react-popper';

import * as menuStyles from './Menu.sty.js';
import { ALIGN_OFFSET } from './SubContent.js';
import { DataTestId } from '../../core/types/data-props.js';
import { WithChildren } from '../../core/types/with-children.js';


/**
 * @public
 */
export type MenuItemTooltipProps = WithChildren & DataTestId;

/** @internal */
export const MenuItemTooltipWrapper = ({
  triggerElement,
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  triggerElement: HTMLElement | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
} & WithChildren) => {
  const [menuTooltipRefRef, setMenuTooltipRefRef] =
    useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(triggerElement, menuTooltipRefRef, {
    placement: 'right-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [ALIGN_OFFSET, 0],
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left-start'],
        },
      },
    ],
  });

  return (
    <div
      ref={setMenuTooltipRefRef}
      style={styles.popper}
      className={menuStyles.tooltipWrapper}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...attributes.popper}
    >
      {children}
    </div>
  );
};
MenuItemTooltipWrapper.displayName = 'MenuItemTooltipWrapper';

/**
 * A tooltip which can be used as a child of an Item. Its content will appear
 * in an overlay when the Item is focused.
 * @public
 */
export const ItemTooltip = forwardRef<HTMLDivElement, MenuItemTooltipProps>(
  ({ children, 'data-testid': dataTestId }, forwardedRef) =>
    children ? (
      <div
        className={menuStyles.tooltip}
        ref={forwardedRef}
        data-testid={dataTestId}
      >
        {children}
      </div>
    ) : null,
);

(ItemTooltip as typeof ItemTooltip & { displayName: string }).displayName =
  'Menu.ItemTooltip';
